import api from '../api';
import { Questionnaire, SubmitQuestionnaireDto, SurveyResponseWithValidationDto } from '../../types/questionnaire';

interface ApiListResponse {
  success: boolean;
  data: Questionnaire[];
  meta: object;
}

interface ApiSingleResponse {
  success: boolean;
  data: Questionnaire;
  meta: object;
}

interface ApiSubmitResponse {
  success: boolean;
  data: SurveyResponseWithValidationDto;
  meta: object;
}

interface ApiHtmlResponse {
    success: boolean;
    data: {
        html: string;
    };
    meta: object;
}

interface InvitePreview {
  templateId: number;
  name: string;
  description: string;
}

interface ApiInvitePreviewResponse {
  success: boolean;
  data: InvitePreview;
  meta: object;
}

interface ApiAcceptInviteResponse {
  success: boolean;
  data: { templateId: number };
  meta: object;
}

export const getQuestionnaires = async (): Promise<Questionnaire[]> => {
  try {
    const response = await api.get<ApiListResponse>('/questionnaires');
    if (response && response.data && response.data.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    console.warn('API response for /questionnaires was not in the expected format:', response.data);
    return [];
  } catch (error) {
    console.error('Failed to get questionnaires:', error);
    return [];
  }
};

export const getQuestionnaireById = async (id: string): Promise<Questionnaire | null> => {
  try {
    const response = await api.get<ApiSingleResponse>(`/questionnaires/${id}`);
    if (response && response.data && response.data.success) {
      return response.data.data;
    }
    console.warn(`API response for /questionnaires/${id} was not in the expected format:`, response.data);
    return null;
  } catch (error) {
    console.error(`Failed to get questionnaire with id ${id}:`, error);
    return null;
  }
};

export const getStartSurvey = async (): Promise<Questionnaire | null> => {
  try {
    const response = await api.get<ApiSingleResponse>('/questionnaires/start-survey');
    if (response && response.data && response.data.success) {
      return response.data.data;
    }
    console.warn('API response for /questionnaires/start-survey was not in the expected format:', response.data);
    return null;
  } catch (error) {
    console.error('Failed to get start survey:', error);
    return null;
  }
};

export const submitQuestionnaire = async (id: string, data: SubmitQuestionnaireDto): Promise<SurveyResponseWithValidationDto> => {
  try {
    const response = await api.post<ApiSubmitResponse>(`/questionnaires/${id}`, data);
    if (response && response.data && response.data.success) {
      return response.data.data;
    }
    // If the API returns a success: false but with validation errors, we should still return the data
    if (response && response.data && response.data.data && response.data.data.validation) {
      return response.data.data;
    }
    console.warn(`API response for POST /questionnaires/${id} was not in the expected format:`, response.data);
    throw new Error('Failed to submit questionnaire: Unexpected API response format');
  } catch (error) {
    console.error(`Failed to submit questionnaire with id ${id}:`, error);
    throw error;
  }
};

export const submitStartSurvey = async (data: SubmitQuestionnaireDto): Promise<SurveyResponseWithValidationDto> => {
  try {
    const response = await api.post<ApiSubmitResponse>('/questionnaires/start-survey', data);
    if (response && response.data && response.data.success) {
      return response.data.data;
    }
    // If the API returns a success: false but with validation errors, we should still return the data
    if (response && response.data && response.data.data && response.data.data.validation) {
      return response.data.data;
    }
    console.warn(`API response for POST /questionnaires/start-survey was not in the expected format:`, response.data);
    throw new Error('Failed to submit start survey: Unexpected API response format');
  } catch (error) {
    console.error('Failed to submit start survey:', error);
    throw error;
  }
};

// New functions for expert view
export const getExpertQuestionnaires = async (clientId: string): Promise<Questionnaire[]> => {
    try {
        const response = await api.get<ApiListResponse>(`/client/${clientId}/questionnaires`);
        if (response && response.data && response.data.success && Array.isArray(response.data.data)) {
            return response.data.data;
        }
        console.warn(`API response for /client/${clientId}/questionnaires was not in the expected format:`, response.data);
        return [];
    } catch (error) {
        console.error(`Failed to get expert questionnaires for client ${clientId}:`, error);
        return [];
    }
};

export const getExpertStartSurvey = async (clientId: string): Promise<Questionnaire | null> => {
    try {
        const response = await api.get<ApiSingleResponse>(`/client/${clientId}/questionnaires/start-survey`);
        if (response && response.data && response.data.success) {
            return response.data.data;
        }
        console.warn(`API response for /client/${clientId}/questionnaires/start-survey was not in the expected format:`, response.data);
        return null;
    } catch (error) {
        console.error(`Failed to get expert start survey for client ${clientId}:`, error);
        return null;
    }
};

export const submitExpertStartSurvey = async (clientId: string, data: SubmitQuestionnaireDto): Promise<SurveyResponseWithValidationDto> => {
    try {
        const response = await api.post<ApiSubmitResponse>(`/client/${clientId}/questionnaires/start-survey`, data);
        if (response && response.data && response.data.success) {
            return response.data.data;
        }
        if (response && response.data && response.data.data && response.data.data.validation) {
            return response.data.data;
        }
        console.warn(`API response for POST /client/${clientId}/questionnaires/start-survey was not in the expected format:`, response.data);
        throw new Error('Failed to submit expert start survey: Unexpected API response format');
    } catch (error) {
        console.error(`Failed to submit expert start survey for client ${clientId}:`, error);
        throw error;
    }
};

export const getExpertQuestionnaireById = async (clientId: string, templateId: string): Promise<Questionnaire | null> => {
    try {
        const response = await api.get<ApiSingleResponse>(`/client/${clientId}/questionnaires/${templateId}`);
        if (response && response.data && response.data.success) {
            return response.data.data;
        }
        console.warn(`API response for /client/${clientId}/questionnaires/${templateId} was not in the expected format:`, response.data);
        return null;
    } catch (error) {
        console.error(`Failed to get expert questionnaire with id ${templateId} for client ${clientId}:`, error);
        return null;
    }
};

export const downloadClientQuestionnairePdf = async (clientId: string, templateId: number, questionnaireName: string): Promise<void> => {
    const response = await api.post(
        `/client/${clientId}/questionnaires/${templateId}/pdf`,
        {},
        { responseType: 'blob' },
    );
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${questionnaireName || 'questionnaire'}-${clientId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
};

export const getInvitePreview = async (token: string): Promise<InvitePreview | null> => {
  try {
    const response = await api.get<ApiInvitePreviewResponse>(`/questionnaires/invite/${token}`);
    if (response && response.data && response.data.success) {
      return response.data.data;
    }
    console.warn(`API response for /questionnaires/invite/${token} was not in the expected format:`, response.data);
    return null;
  } catch (error) {
    console.error(`Failed to get invite preview for token ${token}:`, error);
    return null;
  }
};

export const acceptInvite = async (token: string): Promise<{ templateId: number }> => {
  const response = await api.post<ApiAcceptInviteResponse>(`/questionnaires/invite/${token}/accept`);
  if (response && response.data && response.data.success) {
    return response.data.data;
  }
  throw new Error('Failed to accept invite: Unexpected API response format');
};

export const renderExpertQuestionnairePreview = async (clientId: string, templateId: string): Promise<string | null> => {
    try {
        const response = await api.get<ApiHtmlResponse>(`/client/${clientId}/questionnaires/${templateId}/render-preview`);
        if (response && response.data && response.data.success) {
            return response.data.data.html;
        }
        console.warn(`API response for /client/${clientId}/questionnaires/${templateId}/render-preview was not in the expected format:`, response.data);
        return null;
    } catch (error) {
        console.error(`Failed to render expert questionnaire preview with id ${templateId} for client ${clientId}:`, error);
        return null;
    }
};
